import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import queryString from 'query-string';
import { PrakurtiCirculerLogo } from '../../../../components/imagepath';

/**
 * Custom hook for programme booking table columns configuration
 * @param {Object} props - Hook props
 * @param {Boolean} props.canUpdate - Permission to update status
 * @param {Function} props.onStatusUpdate - Status update handler
 * @returns {Array} Column configuration for AdvancedTable
 */
export const useProgrammeBookingColumns = ({ canUpdate, onStatusUpdate }) => {
    const location = useLocation();
    const params = queryString.parse(location.search);
    const pageSize = 10;

    return useMemo(() => [
        {
            id: 'sr',
            name: '#',
            selector: 'sr',
            width: 50,
            cell: (row, index) => {
                const currentPage = parseInt(params?.page) || 1;
                const calculatedIndex = (currentPage - 1) * pageSize + index + 1;
                return <span style={{ fontWeight: '500', color: '#666' }}>{calculatedIndex}</span>;
            },
        },
        {
            id: 'orderUniqueId',
            name: 'Order ID',
            selector: 'orderUniqueId',
            width: 160,
            cell: (record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {record?.isSimulation && (
                        <Tooltip title="Simulation Mode">
                            <img
                                src={PrakurtiCirculerLogo}
                                alt="sim"
                                style={{ width: '16px', height: '16px', borderRadius: '50%' }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title={`Order: ${record?.orderUniqueId || record?.orderId || '-'} | Click to view details`}>
                        <Link
                            to={`/order-history-overview/${record?.orderId}`}
                            style={{ fontWeight: '500', color: '#1890ff' }}
                        >
                            {record?.orderUniqueId || record?.orderId || '-'}
                        </Link>
                    </Tooltip>
                </div>
            ),
        },
        {
            id: 'customerInfo',
            name: 'Customer',
            selector: 'customerName',
            width: 200,
            cell: (record) => (
                <Tooltip
                    title={
                        <div>
                            <div><strong>Name:</strong> {record?.customerName || '-'}</div>
                            <div><strong>Phone:</strong> {record?.phoneNo || '-'}</div>
                            {record?.parentCustomerName && (
                                <div><strong>Primary:</strong> {record?.parentCustomerName}</div>
                            )}
                        </div>
                    }
                >
                    <div style={{ cursor: 'pointer' }}>
                        <div style={{ fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {record?.customerName || '-'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{record?.phoneNo || '-'}</div>
                        {record?.parentCustomerName && (
                            <div style={{ fontSize: '11px', color: '#1890ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                Primary: {record?.parentCustomerName}
                            </div>
                        )}
                    </div>
                </Tooltip>
            ),
        },
        {
            id: 'programmeData',
            name: 'Programme',
            selector: row => row.programmeData?.[0]?.programmeName || '-',
            cell: record => (
                <Tooltip title={record?.programmeData?.[0]?.programmeName || '-'}>
                    <Tag color="blue" style={{ marginBottom: '2px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {record?.programmeData?.[0]?.programmeName || '-'}
                    </Tag>
                </Tooltip>
            ),
            sortable: true,
            width: 180,
        },
        {
            id: 'schedule',
            name: 'Schedule',
            selector: 'startDate',
            width: 160,
            cell: (record) => {
                const startDate = record.startDate ? dayjs(record.startDate).format('DD MMM YYYY') : '-';
                const endDate = record.endDate ? dayjs(record.endDate).format('DD MMM YYYY') : '-';
                const startShort = record.startDate ? dayjs(record.startDate).format('DD MMM') : '-';
                const endShort = record.endDate ? dayjs(record.endDate).format('DD MMM YY') : '-';
                return (
                    <Tooltip title={`From: ${startDate} | To: ${endDate} | Duration: ${record?.totalDays || 0} days`}>
                        <div style={{ cursor: 'pointer' }}>
                            <div style={{ fontSize: '13px', fontWeight: '500' }}>{startShort} - {endShort}</div>
                            <div style={{ marginTop: '2px' }}>
                                <Tag color="cyan" style={{ fontSize: '11px' }}>
                                    {record?.totalDays || 0} {record?.totalDays === 1 ? 'Day' : 'Days'}
                                </Tag>
                                {record?.isReschedule && (
                                    <Tag color="orange" style={{ fontSize: '10px' }}>Rescheduled</Tag>
                                )}
                            </div>
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            id: 'bookedOn',
            name: 'Booked On',
            selector: 'createdAt',
            width: 110,
            cell: (record) => {
                const fullDate = record?.createdAt ? dayjs(record.createdAt).format('DD MMM YYYY, hh:mm A') : '-';
                return (
                    <Tooltip title={`Booked on: ${fullDate}${record?.createdByName ? ` | By: ${record?.createdByName}` : ''}`}>
                        <div style={{ fontSize: '12px', color: '#666', cursor: 'pointer' }}>
                            {record?.createdAt ? dayjs(record.createdAt).format('DD MMM YY') : '-'}
                            {record?.createdByName && (
                                <div style={{ fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90px' }}>
                                    by {record?.createdByName}
                                </div>
                            )}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            id: 'status',
            name: 'Status',
            selector: 'status',
            width: 120,
            cell: (record) => {
                const status = record?.status || 'Upcoming';
                const colors = {
                    'Upcoming': 'blue',
                    'InProgress': 'orange',
                    'Completed': 'green',
                    'Cancelled': 'red',
                };
                return <Tag color={colors[status] || 'default'}>{status}</Tag>;
            },
        },
        // {
        //     id: 'actions',
        //     name: 'Actions',
        //     width: 200,
        //     cell: (record) => (
        //         <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        //             {canUpdate && record?.status !== 'Completed' && record?.status !== 'Cancelled' && (
        //                 <Tooltip title="Update booking status">
        //                     <div style={{ width: '130px' }}>
        //                         <InputFromUser
        //                             type="singleSelect"
        //                             name="status"
        //                             onChange={newStatus => onStatusUpdate(record.id || record._id, newStatus)}
        //                             options={['Upcoming', 'InProgress', 'Completed', 'Cancelled']}
        //                             FormGroupClassName={'cursor-pointer mb-0'}
        //                             value={record.status}
        //                             label=""
        //                         />
        //                     </div>
        //                 </Tooltip>
        //             )}
        //         </div>
        //     ),
        // },
    ], [canUpdate, onStatusUpdate, params?.page]);
};
